import { useMemo } from 'react';

import { useGetCoveragesQuery } from '@/services/api/coverageService';
import { useGetTasksQuery } from '@/services/api/tasksService';
import { Task } from '@/services/types/client-intake-types';
import {
  ContactHandleSchema,
  CoverageCategory,
  CoverageSchema,
} from '@/services/types/coverage-types';
import { CanonicalTaskGroup } from '@/services/types/task-types';
import { isPhoneNumberValid } from '@/utils/validation';

/** Insurance-related task groups that have coverages */
const INSURANCE_TASK_GROUPS = [
  CanonicalTaskGroup.FIRST_PARTY_INSURANCE,
  CanonicalTaskGroup.THIRD_PARTY_INSURANCE,
];

/**
 * Contact extracted from a coverage, with associated task info for AI Hold & Transfer.
 */
export interface CarrierContactWithTask {
  id: string;
  /** Carrier/adjuster/subrogator name */
  name: string;
  /** Secondary label (e.g., "Claims Line", "Adjuster") */
  secondaryTitle?: string;
  /** Phone number */
  phone: string;
  /** Contact type */
  type: 'carrier' | 'adjuster' | 'subrogator';
  /** Coverage ID this contact belongs to */
  coverageId: string;
  /** Coverage category (1P, 3P, etc.) */
  coverageCategory: CoverageCategory | null;
  /** Associated task ID - required for starting Voice AI workflow */
  taskId: string;
  /** Task name for display */
  taskName: string;
  /** Task type name for display */
  taskTypeName: string | null;
  /** Carrier name for display (may differ from contact name) */
  carrierName: string | null;
}

interface UseCarrierTasksForHoldTransferResult {
  /** All contacts from insurance tasks, with task IDs attached */
  contacts: CarrierContactWithTask[];
  /** Carrier contacts only */
  carrierContacts: CarrierContactWithTask[];
  /** Adjuster + subrogator contacts */
  adjusterContacts: CarrierContactWithTask[];
  /** Whether data is still loading */
  isLoading: boolean;
  /** Number of insurance tasks with coverages found */
  taskCount: number;
}

/**
 * Extract phone contacts from a coverage and attach task info.
 */
function extractContactsFromCoverage(
  coverage: CoverageSchema,
  task: Task,
): CarrierContactWithTask[] {
  const contacts: CarrierContactWithTask[] = [];
  const carrierName = coverage.carrier?.name || null;
  const taskTypeName = task.type?.name || null;

  // Carrier contacts
  if (coverage.carrier?.handles) {
    const phoneHandles = coverage.carrier.handles.filter(
      (handle: ContactHandleSchema) =>
        handle.handle_type === 'PHONE' &&
        handle.value &&
        isPhoneNumberValid(handle.value),
    );

    phoneHandles.forEach((handle, index) => {
      contacts.push({
        id: `task-${task.id}-coverage-${coverage.id}-carrier-${index}`,
        name: coverage.carrier?.name || 'Unknown Carrier',
        secondaryTitle: handle.name || 'Carrier',
        phone: handle.value,
        type: 'carrier',
        coverageId: coverage.id,
        coverageCategory: coverage.category,
        taskId: task.id,
        taskName: task.name,
        taskTypeName,
        carrierName,
      });
    });
  }

  // Adjuster contacts
  if (coverage.adjusters?.[0]?.handles) {
    const phoneHandles = coverage.adjusters[0].handles.filter(
      (handle: ContactHandleSchema) =>
        handle.handle_type === 'PHONE' &&
        handle.value &&
        isPhoneNumberValid(handle.value),
    );

    phoneHandles.forEach((handle, index) => {
      contacts.push({
        id: `task-${task.id}-coverage-${coverage.id}-adjuster-${index}`,
        name: coverage.adjusters?.[0]?.name || 'Unknown Adjuster',
        secondaryTitle: handle.name || 'Adjuster',
        phone: handle.value,
        type: 'adjuster',
        coverageId: coverage.id,
        coverageCategory: coverage.category,
        taskId: task.id,
        taskName: task.name,
        taskTypeName,
        carrierName,
      });
    });
  }

  // Subrogator contacts
  if (coverage.subrogator?.handles) {
    const phoneHandles = coverage.subrogator.handles.filter(
      (handle: ContactHandleSchema) =>
        handle.handle_type === 'PHONE' &&
        handle.value &&
        isPhoneNumberValid(handle.value),
    );

    phoneHandles.forEach((handle, index) => {
      contacts.push({
        id: `task-${task.id}-coverage-${coverage.id}-subrogator-${index}`,
        name: coverage.subrogator?.name || 'Unknown Subrogator',
        secondaryTitle: handle.name || 'Subrogator',
        phone: handle.value,
        type: 'subrogator',
        coverageId: coverage.id,
        coverageCategory: coverage.category,
        taskId: task.id,
        taskName: task.name,
        taskTypeName,
        carrierName,
      });
    });
  }

  return contacts;
}

/**
 * Hook to fetch insurance tasks and extract carrier contacts for AI Hold & Transfer.
 *
 * This hook:
 * 1. Fetches all 1P and 3P insurance tasks for the matter
 * 2. For each task with a linked coverage, extracts carrier/adjuster/subrogator contacts
 * 3. Returns contacts with their associated task IDs (required for starting Voice AI workflows)
 *
 * @param matterId - The matter ID to fetch tasks for
 * @returns Contacts with task associations, loading state, and task count
 */
export const useCarrierTasksForHoldTransfer = (
  matterId: string,
): UseCarrierTasksForHoldTransferResult => {
  // Fetch insurance tasks (1P and 3P) for this matter
  const { data: tasksData, isLoading: tasksLoading } = useGetTasksQuery(
    {
      matter: matterId,
      canonical_groups: INSURANCE_TASK_GROUPS,
      limit: 100, // Should be plenty for a single matter
    },
    { skip: !matterId },
  );

  // Fetch all coverages for the matter (to get full coverage details)
  const { data: coverages, isLoading: coveragesLoading } = useGetCoveragesQuery(
    { matterId },
    { skip: !matterId },
  );

  // Build a map of coverage ID -> CoverageSchema for quick lookup
  const coverageMap = useMemo(() => {
    if (!coverages) return new Map<string, CoverageSchema>();
    return new Map(coverages.map((c) => [c.id, c]));
  }, [coverages]);

  // Filter tasks that have coverages and extract contacts
  const { contacts, tasksWithCoverages } = useMemo(() => {
    if (!tasksData?.results || !coverages) {
      return { contacts: [], tasksWithCoverages: 0 };
    }

    const allContacts: CarrierContactWithTask[] = [];
    let taskCount = 0;

    tasksData.results.forEach((task) => {
      // Task may have coverage_id or coverage object
      const coverageId = task.coverage_id || task.coverage?.id;
      if (!coverageId) return;

      const coverage = coverageMap.get(coverageId);
      if (!coverage) return;

      taskCount += 1;
      const taskContacts = extractContactsFromCoverage(coverage, task);
      allContacts.push(...taskContacts);
    });

    return { contacts: allContacts, tasksWithCoverages: taskCount };
  }, [tasksData?.results, coverages, coverageMap]);

  // Categorize contacts
  const carrierContacts = useMemo(
    () => contacts.filter((c) => c.type === 'carrier'),
    [contacts],
  );

  const adjusterContacts = useMemo(
    () => contacts.filter((c) => c.type === 'adjuster' || c.type === 'subrogator'),
    [contacts],
  );

  return {
    contacts,
    carrierContacts,
    adjusterContacts,
    isLoading: tasksLoading || coveragesLoading,
    taskCount: tasksWithCoverages,
  };
};
